import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowUpRight, Search } from 'lucide-react';
import { blogPosts } from '../mock';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BlogIndexPage = ({ onOpenBooking, onOpenLogin }) => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');

  const categories = useMemo(() => {
    const set = new Set(blogPosts.map((p) => p.category));
    return ['All', ...Array.from(set)];
  }, []);

  const filtered = useMemo(() => {
    return blogPosts.filter((p) => {
      const matchesCat = category === 'All' || p.category === category;
      const q = query.trim().toLowerCase();
      const matchesQ =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q);
      return matchesCat && matchesQ;
    });
  }, [query, category]);

  const featured = filtered[filtered.length - 1];
  const rest = filtered.slice(0, -1);
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header onLogin={onOpenLogin} onCtaBook={onOpenBooking} />

      <section className="relative pt-32 pb-14">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <p className="text-amber-400 tracking-[0.32em] text-[12px] mb-5 font-medium">BLOG</p>
          <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-[72px] leading-[1.05] tracking-tight max-w-3xl">
            From the <span className="text-amber-400 italic">study room</span>
          </h1>
          <p className="mt-6 text-white/65 text-lg max-w-2xl leading-relaxed">
            Lessons, strategies, and stories from years of teaching and playing Go, written to
            help you actually improve.
          </p>

          {/* Toolbar */}
          <div className="mt-12 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles…"
                className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-11 pr-4 py-3 text-white placeholder:text-white/35 focus:outline-none focus:border-amber-400 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                    category === c
                      ? 'border-amber-400 bg-amber-400 text-[#0a0a0a]'
                      : 'border-white/15 text-white/70 hover:border-amber-400/50 hover:text-amber-400'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative pb-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          {filtered.length === 0 && (
            <div className="text-center py-20 text-white/55">
              No articles match your search. Try a different keyword.
            </div>
          )}

          {featured && (
            <Link
              to={`/blog/${featured.slug}`}
              className="group block rounded-2xl overflow-hidden bg-white/[0.02] border border-white/10 hover:border-amber-400/40 transition-all duration-300 mb-10"
            >
              <div className="grid lg:grid-cols-2">
                <div className="relative h-72 lg:h-auto overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-5 left-5 px-3 py-1 rounded-full bg-amber-400 text-[#0a0a0a] text-xs font-medium tracking-wide">
                    Featured
                  </div>
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="inline-flex items-center w-fit gap-2 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-400 text-xs font-medium tracking-wider">
                    {featured.category}
                  </div>
                  <h2 className="mt-3 font-serif text-white text-3xl lg:text-4xl leading-[1.15] group-hover:text-amber-400 transition-colors">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-white/65 leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-6 flex items-center gap-4 text-white/50 text-sm">
                    <span>{featured.date}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                       {featured.author}
                    </span>
                  </div>
                  <div className="mt-7 inline-flex items-center gap-2 text-amber-400 text-sm group-hover:gap-3 transition-all">
                    Read article <ArrowUpRight size={15} />
                  </div>
                </div>
              </div>
            </Link>
          )}

          {rest.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {rest.reverse().map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group rounded-2xl overflow-hidden bg-white/[0.02] border border-white/10 hover:border-amber-400/40 transition-all duration-300"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/80 to-transparent" />
                    <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-amber-400/90 text-[#0a0a0a] text-xs font-medium tracking-wide">
                      {p.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-white/50 text-xs">
                      <span>{p.date}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        {featured.author}
                      </span>
                    </div>
                    <h3 className="mt-3 font-serif text-white text-2xl leading-[1.25] group-hover:text-amber-400 transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-white/60 text-[14.5px] leading-relaxed line-clamp-2">
                      {p.excerpt}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 text-amber-400 text-sm group-hover:gap-3 transition-all">
                      Read article <ArrowUpRight size={15} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogIndexPage;

